// In-editor user guide (? / F1): full-screen overlay with a section
// nav, a search box that filters entries across all sections, and a
// keyboard-shortcut table generated LIVE from the command registry —
// the table can never drift from the real bindings. Guide prose lives
// in lib/help-content.ts.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  shortcutLabel,
  type EditorCommand,
} from "../lib/commands.ts";
import {
  HELP_GESTURES,
  HELP_SECTIONS,
  type HelpEntry,
  type HelpSection,
} from "../lib/help-content.ts";

const SHORTCUTS_ID = "shortcuts";

function entryMatches(e: HelpEntry, q: string): boolean {
  if (e.title.toLowerCase().includes(q)) return true;
  if (e.kbd?.toLowerCase().includes(q)) return true;
  return e.body.some((p) => p.toLowerCase().includes(q));
}

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)] px-1.5 py-0.5 font-mono text-[11px] text-[color:var(--color-fg-muted)]">
      {children}
    </kbd>
  );
}

function EntryView({ entry }: { entry: HelpEntry }) {
  // Consecutive "• " lines render as one list.
  const chunks: { list: boolean; lines: string[] }[] = [];
  for (const line of entry.body) {
    const list = line.startsWith("• ");
    const last = chunks[chunks.length - 1];
    if (last && last.list === list) last.lines.push(line);
    else chunks.push({ list, lines: [line] });
  }
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline gap-2">
        <h4 className="text-sm font-semibold">{entry.title}</h4>
        {entry.kbd && <Kbd>{entry.kbd}</Kbd>}
      </div>
      {chunks.map((c, i) =>
        c.list ? (
          <ul key={i} className="list-disc space-y-1 pl-5 text-sm text-[color:var(--color-fg-muted)]">
            {c.lines.map((l, j) => (
              <li key={j}>{l.slice(2)}</li>
            ))}
          </ul>
        ) : (
          c.lines.map((l, j) => (
            <p key={`${i}-${j}`} className="text-sm text-[color:var(--color-fg-muted)]">
              {l}
            </p>
          ))
        )
      )}
    </div>
  );
}

export function HelpOverlay({
  commands,
  onClose,
}: {
  commands: readonly EditorCommand[];
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [section, setSection] = useState<string>(HELP_SECTIONS[0]!.id);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Esc closes the guide without also clearing the editor selection —
  // capture phase beats the global shortcut handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // One row per command label from the live registry. The four
  // per-direction nudge commands collapse into one hand-written row.
  const shortcutRows = useMemo(() => {
    const rows: { keys: string; does: string }[] = [];
    const seen = new Set<string>();
    for (const c of commands) {
      if (!c.shortcut || c.id.startsWith("edit.nudge")) continue;
      if (seen.has(c.label)) continue;
      seen.add(c.label);
      rows.push({ keys: shortcutLabel(c.shortcut), does: c.label });
    }
    rows.push({ keys: "Arrows / Shift+Arrows", does: "Nudge selection 1 pt / 10 pt" });
    return rows;
  }, [commands]);

  const q = query.trim().toLowerCase();

  // While searching: sections shrink to their matching entries.
  const visible = useMemo(() => {
    if (!q) return HELP_SECTIONS as readonly (HelpSection & { entries: HelpEntry[] })[];
    return HELP_SECTIONS.map((s) => ({
      ...s,
      entries: s.entries.filter((e) => entryMatches(e, q)),
    })).filter((s) => s.entries.length > 0);
  }, [q]);

  const shortcutsMatch =
    !q ||
    "keyboard shortcuts".includes(q) ||
    shortcutRows.some(
      (r) => r.keys.toLowerCase().includes(q) || r.does.toLowerCase().includes(q)
    ) ||
    HELP_GESTURES.some(
      (r) => r.keys.toLowerCase().includes(q) || r.does.toLowerCase().includes(q)
    );

  const goTo = (id: string) => {
    setSection(id);
    bodyRef.current
      ?.querySelector(`[data-help-section="${id}"]`)
      ?.scrollIntoView({ block: "start" });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Editor guide"
      className="absolute inset-0 z-50 flex flex-col bg-[color:var(--color-bg)]"
    >
      {/* header */}
      <div className="flex items-center gap-3 border-b border-[color:var(--color-border)] px-4 py-2.5">
        <i className="ri-question-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-base font-semibold">Editor guide</h2>
        <div className="ml-4 flex min-w-0 flex-1 items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-subtle)] px-2">
          <i className="ri-search-line text-[color:var(--color-fg-muted)]" aria-hidden />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Search the guide — try “redact”, “snap”, “password”…"
            aria-label="Search the guide"
            className="h-8 min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQuery("")}
              className="text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]"
            >
              <i className="ri-close-circle-line" aria-hidden />
            </button>
          )}
        </div>
        <span className="hidden text-[10px] text-[color:var(--color-fg-muted)] sm:inline">Esc</span>
        <button
          type="button"
          title="Close guide"
          aria-label="Close guide"
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* section nav */}
        <nav
          aria-label="Guide sections"
          className="hidden w-56 shrink-0 space-y-0.5 overflow-y-auto border-r border-[color:var(--color-border)] p-2 sm:block"
        >
          {HELP_SECTIONS.map((s) => {
            const dimmed = q !== "" && !visible.some((v) => v.id === s.id);
            return (
              <button
                key={s.id}
                type="button"
                aria-current={section === s.id ? "true" : undefined}
                onClick={() => goTo(s.id)}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${
                  section === s.id
                    ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                    : dimmed
                      ? "text-[color:var(--color-fg-muted)] opacity-40"
                      : "hover:bg-[color:var(--color-bg-subtle)]"
                }`}
              >
                <i className={`${s.icon} shrink-0`} aria-hidden />
                <span className="truncate">{s.title}</span>
              </button>
            );
          })}
          <button
            type="button"
            aria-current={section === SHORTCUTS_ID ? "true" : undefined}
            onClick={() => goTo(SHORTCUTS_ID)}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${
              section === SHORTCUTS_ID
                ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                : q && !shortcutsMatch
                  ? "text-[color:var(--color-fg-muted)] opacity-40"
                  : "hover:bg-[color:var(--color-bg-subtle)]"
            }`}
          >
            <i className="ri-keyboard-line shrink-0" aria-hidden />
            <span className="truncate">Keyboard shortcuts</span>
          </button>
        </nav>

        {/* content */}
        <div ref={bodyRef} className="min-w-0 flex-1 overflow-y-auto scroll-p-4 p-4 sm:p-6">
          {q && visible.length === 0 && !shortcutsMatch && (
            <p className="py-10 text-center text-sm text-[color:var(--color-fg-muted)]">
              Nothing in the guide matches “{query}”.
            </p>
          )}
          <div className="mx-auto max-w-2xl space-y-8">
            {visible.map((s) => (
              <section key={s.id} data-help-section={s.id} className="space-y-3">
                <div className="space-y-1 border-b border-[color:var(--color-border)] pb-2">
                  <h3 className="flex items-center gap-2 text-base font-semibold">
                    <i className={`${s.icon} text-[color:var(--color-fg-muted)]`} aria-hidden />
                    {s.title}
                  </h3>
                  <p className="text-xs text-[color:var(--color-fg-muted)]">{s.blurb}</p>
                </div>
                <div className="space-y-4">
                  {s.entries.map((e) => (
                    <EntryView key={e.title} entry={e} />
                  ))}
                </div>
              </section>
            ))}

            {shortcutsMatch && (
              <section data-help-section={SHORTCUTS_ID} className="space-y-3">
                <div className="space-y-1 border-b border-[color:var(--color-border)] pb-2">
                  <h3 className="flex items-center gap-2 text-base font-semibold">
                    <i className="ri-keyboard-line text-[color:var(--color-fg-muted)]" aria-hidden />
                    Keyboard shortcuts
                  </h3>
                  <p className="text-xs text-[color:var(--color-fg-muted)]">
                    Generated from the editor's live command registry — always current.
                    Single letters work when focus isn't in a text field.
                  </p>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {shortcutRows.map((r) => (
                      <tr key={`${r.keys}-${r.does}`} className="border-b border-[color:var(--color-border)] last:border-0">
                        <td className="w-40 py-1.5 pr-3 align-top">
                          <Kbd>{r.keys}</Kbd>
                        </td>
                        <td className="py-1.5 text-[color:var(--color-fg-muted)]">{r.does}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h4 className="pt-2 text-sm font-semibold">Mouse & gestures</h4>
                <table className="w-full text-sm">
                  <tbody>
                    {HELP_GESTURES.map((r) => (
                      <tr key={r.keys} className="border-b border-[color:var(--color-border)] last:border-0">
                        <td className="w-40 py-1.5 pr-3 align-top">
                          <Kbd>{r.keys}</Kbd>
                        </td>
                        <td className="py-1.5 text-[color:var(--color-fg-muted)]">{r.does}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
